/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect } from "react";
import { Button, TextField } from "@material-ui/core";
import ScheduledReportCard from "./ScheduledReportCard";
import { useScheduledReports } from "../context/scheduledReports";
import { EmptyPlaceholder } from "../../../components";
import Connect from "../../../utility/connect-endpoints";
import { isValidEmail } from "../../../utility";
import { PageHeader } from "../../../components/Common";

/**
 * This component displays a card for editing the list of scheduled report.
 *
 * @param {Array} scheduledReports A list of scheduled reports configuration objects.
 * @param {string} searchTerm A search term to filter the result.
 */
const validateScheduledReport = (scheduledReports) => {
	if (!Array.isArray(scheduledReports)) {
		return false;
	}
	let response = true;
	for (let index = 0; index < scheduledReports.length; index++) {
		const report = scheduledReports[index];
		if (!report.reportPath || report.emailAddressList.length === 0) {
			response = false;
			break;
		}
		if (report.emailAddressList.find((email) => !isValidEmail(email))) {
			response = false;
			break;
		}
		if (scheduledReports.filter((x) => x.reportPath.includes(report.reportPath)).length > 1) {
			response = false;
			break;
		}
		if (!report.reportPath.endsWith("-YYYY-MM-DDThh:mm:ssZ.csv")) {
			response = false;
			break;
		}
	}
	return response;
};

export const ScheduledReportSettings = ({ setScheduleReportModified, scheduledReports }) => {
	const { loading, addScheduledReports, saveScheduledReportsList, clearScheduledReports } = useScheduledReports();
	const [formEdited, setFormEdited] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [validation, setValidation] = useState(false);

	useEffect(() => {
		setScheduleReportModified(formEdited);
	}, [formEdited, setScheduleReportModified]);

	const handleAddScheduledReport = () => {
		//Creating new scheduled report
		const newScheduledReport = {
			reportId: "PACE_NEW_REPORT",
			reportName: "",
			reportPath: "",
			emailAddressList: [],
			active: true,
		};
		addScheduledReports(newScheduledReport);
	};
	useEffect(() => {
		setValidation(validateScheduledReport(scheduledReports));
	}, [scheduledReports]);

	const lowerCaseSearchTerm = searchTerm.toLowerCase();
	const searchFilteredScheduledReports =
		scheduledReports &&
		scheduledReports.reduce((filtered, report, index) => {
			if (
				report.reportPath.toLowerCase().includes(lowerCaseSearchTerm) ||
				report.emailAddressList.find((emailAddressList) => emailAddressList.toLowerCase().includes(lowerCaseSearchTerm))
			) {
				filtered.push(
					<div key={report.reportId + index}>
						<ScheduledReportCard
							scheduledReport={report}
							scheduledReports={scheduledReports}
							formEdited={formEdited}
							setFormEdited={setFormEdited}
							reportIndex={index}
						/>
					</div>,
				);
			}
			return filtered;
		}, []);

	return (
		<>
			<div style={{ minWidth: 820, padding: "15px", marginTop: "15px" }}>
				<div>
					<div style={{ position: "relative", minHeight: 300, display: "flex", flexDirection: "column" }}>
						<>
							<div className="d-flex justify-content-between">
								<div>
									<PageHeader
										pageTitle="Scheduled Reports"
										pageDescription={
											<span className="text-muted">
												Configure automated emailing for scheduled reports created by{"  "}
												<a href={`${Connect.connectConsole}/metrics-reports`} target={"_blank"} rel="noreferrer">
													Amazon Connect.
												</a>
												{"  "}
											</span>
										}
									/>
								</div>
								<div style={{ paddingRight: "0px" }} className="col-4 d-flex justify-content-end">
									<Button
										variant="outlined"
										disabled={loading}
										style={{ height: 30 }}
										onClick={() => {
											handleAddScheduledReport();
											setFormEdited(true);
										}}
									>
										Add Report
									</Button>
									{formEdited && (
										<>
											<Button
												className="mx-2"
												variant="outlined"
												style={{ height: 30 }}
												onClick={() => {
													clearScheduledReports();
													setFormEdited(false);
												}}
												disabled={loading}
											>
												Cancel
											</Button>
											<Button
												onClick={() => {
													saveScheduledReportsList();
													setFormEdited(false);
												}}
												disabled={!validation || loading}
												variant="contained"
												style={{
													color: "#fff",
													height: 30,
													backgroundColor: !validation ? "#e99e9f" : "#cc1f20",
													borderColor: "#cc1f20",
												}}
											>
												Save
											</Button>
										</>
									)}
								</div>
							</div>
							{loading ? (
								<i
									style={{ left: "50%", position: "absolute", top: "50%" }}
									className="fas fa-spinner loading-spinner spin-animation"
								/>
							) : scheduledReports.length === 0 ? (
								<EmptyPlaceholder inputStyles={{ margin: "100px 0px" }} message="No reports found." />
							) : (
								<div style={{ flex: 1 }} className="mt-2">
									<TextField
										style={{ width: "70%", marginBottom: "20px" }}
										size="small"
										label="Search report by file name or email"
										value={searchTerm}
										onChange={(event) => setSearchTerm(event.target.value)}
									/>
									<div>
										{searchFilteredScheduledReports.length !== 0 ? (
											searchFilteredScheduledReports
										) : (
											<EmptyPlaceholder message="No report configuration found." />
										)}
									</div>
								</div>
							)}
						</>
					</div>
				</div>
			</div>
		</>
	);
};
